/**
 * Client-side SHA-256 helpers.
 *
 * Uses the Web Crypto API (window.crypto.subtle) so nothing leaves the
 * browser — the user's file is never uploaded to any server.
 *
 * For very large files the v1 implementation reads the full ArrayBuffer
 * before hashing. That's fine up to ~256 MB on most desktops; above
 * that, switch to a streaming approach (split into chunks, hash with
 * @noble/hashes which is already a dependency).
 */

export async function sha256Hex(input: ArrayBuffer | Uint8Array | string): Promise<string> {
  let bytes: ArrayBuffer;
  if (typeof input === 'string') {
    // Copy into a fresh ArrayBuffer so the type lines up with BufferSource.
    const enc = new TextEncoder().encode(input);
    bytes = enc.buffer.slice(enc.byteOffset, enc.byteOffset + enc.byteLength) as ArrayBuffer;
  } else if (input instanceof Uint8Array) {
    bytes = input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength) as ArrayBuffer;
  } else {
    bytes = input;
  }
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return bytesToHex(new Uint8Array(digest));
}

export async function sha256OfFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  return sha256Hex(buffer);
}

export function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i] ?? 0;
    out += b.toString(16).padStart(2, '0');
  }
  return out;
}

export function shortHash(hash: string, head = 8, tail = 8): string {
  if (hash.length <= head + tail + 1) return hash;
  return `${hash.slice(0, head)}…${hash.slice(-tail)}`;
}

export function downloadJson(filename: string, content: unknown) {
  const blob = new Blob([JSON.stringify(content, null, 2)], {
    type: 'application/json',
  });
  triggerDownload(filename, blob);
}

export function downloadText(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  triggerDownload(filename, blob);
}

function triggerDownload(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after the click so the download can complete.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
